// ACC Academy — a plain-English course explaining how FIS configuration (ACC)
// works, written for non-technical staff. Content is distilled from the FIS
// Prepaid ACC Specification v1.26 and deliberately avoids jargon.
//
// Block types used by the renderer:
//   { type:'p', text }                      paragraph
//   { type:'h', text }                      sub-heading
//   { type:'analogy', text }                "in plain terms" box
//   { type:'callout', tone, title, text }   tone: info | tip | warn | rule
//   { type:'list', items:[...] }            bullet list
//   { type:'steps', items:[{title,text}] }  numbered steps
//   { type:'terms', items:[{term,def}] }    inline mini-glossary
//   { type:'table', head:[...], rows:[[..]] }
//   { type:'faq', items:[{q,a}] }

export const COURSE = [
  // ============================================================ MODULE 1
  {
    id: 'start', title: 'Getting Started', icon: 'Rocket',
    lessons: [
      {
        id: 'what-is-acc',
        title: 'What is FIS configuration (ACC)?',
        summary: 'The big idea: how you tell FIS to set up and change benefit cards.',
        minutes: 3,
        keywords: ['acc', 'automated client configuration', 'overview', 'what is', 'file', 'xml'],
        blocks: [
          { type: 'p', text: 'FIS runs the “engine” behind your benefit cards — it decides what each card can buy, how much money it holds, and when that money expires. “ACC” (Automated Client Configuration) is simply the way you send instructions to that engine.' },
          { type: 'p', text: 'You don’t click buttons inside FIS. Instead you prepare a small instruction file and hand it to FIS, which reads it and makes the changes. The tools in this app build that file for you, so you never have to write it by hand.' },
          { type: 'analogy', text: 'Think of ACC like placing an order at a kitchen. You fill out an order ticket (“set this purse to expire Dec 31”, “block this country”), hand it through the window, and the kitchen (FIS) makes it happen. Your job is a clear, correct ticket — that’s all this app does.' },
          { type: 'callout', tone: 'info', title: 'Who uses this', text: 'ACC is used for benefit and insurance card programs (the “Vulture/Hawk” style of card). It manages clients, programs, sub-programs, purses, cards, fees, merchants and more.' },
        ],
        cta: { label: 'Open the Setup Wizard', section: 'acc-wizard' },
      },
      {
        id: 'big-picture',
        title: 'The big picture: how it all fits together',
        summary: 'Clients, programs, sub-programs and purses — the family tree.',
        minutes: 4,
        keywords: ['hierarchy', 'client', 'program', 'sub-program', 'purse', 'structure', 'tree'],
        blocks: [
          { type: 'p', text: 'Everything in FIS is organized like a family tree. Each level lives inside the one above it. Understanding this tree makes everything else click.' },
          { type: 'terms', items: [
            { term: 'Client', def: 'The company or group at the top — your organization or one of its sub-groups.' },
            { term: 'Program', def: 'A benefits offering that belongs to a client (e.g. “2026 Benefits”).' },
            { term: 'Sub-program', def: 'A specific variant with its own rules (e.g. the FSA plan vs. the HSA plan). Where most setup happens.' },
            { term: 'Purse', def: 'A labeled pot of money on the card (FSA pot, transit pot…). A card can have several.' },
          ] },
          { type: 'analogy', text: 'Picture a company → its benefits plan → a specific version of that plan → and several labeled envelopes of money inside it. Money lives in the envelopes (purses); the rules about how it’s spent live on the sub-program.' },
          { type: 'callout', tone: 'rule', title: 'Order matters', text: 'Because each level lives inside the one above, you always set them up top-down: client → program → sub-program → purses. The tools handle this ordering for you.' },
          { type: 'p', text: 'Off to the side there are shared things programs use — card designs (“packages”), merchant lists, and category groups. We cover those later.' },
        ],
      },
      {
        id: 'file-journey',
        title: 'How a change actually happens',
        summary: 'The journey of an instruction file from your screen to FIS and back.',
        minutes: 3,
        keywords: ['moveit', 'landing zone', 'log', 'error', 'process', 'journey', 'submit', 'send', 'sysadmiral', 'acclogs'],
        blocks: [
          { type: 'steps', items: [
            { title: 'You create the file', text: 'Use a tool in this app. It produces a small text file (an “.XML” file) — a tidy list of instructions.' },
            { title: 'You drop it off', text: 'The file is placed in a secure FIS folder (the “MoveIT landing zone”) — an inbox FIS watches.' },
            { title: 'FIS picks it up', text: 'A process called SysAdmiral notices new files (usually within 1–15 minutes), reads them, and applies the changes.' },
            { title: 'You get two results back', text: 'A “Log” file echoes everything that succeeded; an “Error” file lists anything that failed (with a reason). You retrieve these from the “AccLogs” folder.' },
          ] },
          { type: 'callout', tone: 'tip', title: 'Files run in alphabetical order', text: 'If several files wait, FIS runs them alphabetically — not in the order you dropped them. If one change must happen before another, name files carefully or combine them into one.' },
          { type: 'callout', tone: 'warn', title: 'A failed file isn’t the end of the world', text: 'FIS rejects just the bad parts and tells you why. You fix it and resend. Nothing breaks silently.' },
        ],
      },
      {
        id: 'anatomy',
        title: 'What’s actually inside the file',
        summary: 'A peek under the hood — so the structure isn’t a mystery.',
        minutes: 3,
        keywords: ['batch', 'business partner', 'node', 'element', 'response', 'structure', 'anatomy'],
        blocks: [
          { type: 'p', text: 'You never have to write this, but it helps to recognize the shape. Every file is one “Batch” addressed to your company, containing a list of instruction blocks called “nodes.”' },
          { type: 'terms', items: [
            { term: 'Batch', def: 'The whole file. It names your company (the “Business Partner”) and wraps everything else.' },
            { term: 'Node', def: 'One instruction block — a Client, a Purse, a Fee, and so on. A file can hold many.' },
            { term: 'Element', def: 'A single field inside a node — like a purse’s expiry date.' },
            { term: 'Response', def: 'A field FIS fills in on the way back to tell you what happened. You leave it blank.' },
          ] },
          { type: 'analogy', text: 'The Batch is the envelope (addressed to your company), each node is a separate form inside it, and elements are the fields on each form.' },
          { type: 'callout', tone: 'info', title: 'Why this matters', text: 'When you read a Log or Error file, you’ll see these names. Knowing “node = one instruction” makes the results easy to scan.' },
        ],
      },
      {
        id: 'domains',
        title: 'Domains: why defaults differ',
        summary: 'Why two programs can behave differently out of the box.',
        minutes: 2,
        keywords: ['domain', 'domain 3', 'domain 12', 'base', 'defaults'],
        blocks: [
          { type: 'p', text: 'Behind the scenes FIS groups programs into “domains” — most commonly Domain 3, Domain 12, and “base.” A domain sets the sensible defaults and which advanced features are available.' },
          { type: 'analogy', text: 'A domain is like the region your account is set up in: same product, but different default settings and a few region-only features.' },
          { type: 'callout', tone: 'info', title: 'You rarely choose this', text: 'Your domain was decided at launch. It mostly explains why some options apply to one program and not another (e.g. certain pharmacy or real-time features are Domain 3 or Domain 12 only).' },
        ],
      },
    ],
  },

  // ============================================================ MODULE 2
  {
    id: 'tree', title: 'The Core Tree', icon: 'Boxes',
    lessons: [
      {
        id: 'clients',
        title: 'Clients & the hierarchy',
        summary: 'The top of the tree — levels, alternate IDs, and pharmacy partners.',
        minutes: 4,
        keywords: ['client', 'level', 'tree level', 'parent', 'ppdb', 'ces', 'clientspecificid', 'uniquelevel', 'pbm'],
        blocks: [
          { type: 'p', text: 'A Client is a company or group in the FIS tree. Clients sit at “levels” 2 through 9 — a higher number means it sits deeper inside a parent. New clients attach under a named parent.' },
          { type: 'h', text: 'Alternate IDs' },
          { type: 'p', text: 'A client can carry an alternate ID that your downstream systems recognize (formats called PPDB, CES, or a custom “client-specific ID”). A “unique level” says how widely that ID must be unique — across the whole business partner (“Top”), within its parent (“Parent”), or not enforced (“None”).' },
          { type: 'h', text: 'Pharmacy partners (PBMs)' },
          { type: 'p', text: 'On pharmacy-style programs (Domain 12), a client can list the Pharmacy Benefit Managers (PBMs) it works with, so FIS prepares eligibility data for each.' },
          { type: 'callout', tone: 'info', title: 'Mostly stable', text: 'Clients are the foundation and rarely change. Day-to-day work happens further down, on sub-programs and purses.' },
        ],
      },
      {
        id: 'programs',
        title: 'Programs & their dates',
        summary: 'The thin layer between a client and its sub-programs.',
        minutes: 2,
        keywords: ['program', 'effective date', 'start', 'end', 'closeout', 'report name'],
        blocks: [
          { type: 'p', text: 'A Program is a benefits offering attached to a client. It mainly carries dates and a report name — it’s a container that sub-programs live inside.' },
          { type: 'terms', items: [
            { term: 'Effective / Start / End / Close-out dates', def: 'The lifespan of the program.' },
            { term: 'Report name', def: 'The label used on reports; usually matches the program name and must be unique.' },
          ] },
          { type: 'callout', tone: 'tip', title: 'Set once, rarely touched', text: 'Programs are typically created at launch and seldom changed afterward.' },
        ],
      },
      {
        id: 'sub-programs',
        title: 'Sub-programs: the heart of setup',
        summary: 'Where the real rules live — including VULTURE vs HAWK.',
        minutes: 5,
        keywords: ['sub-program', 'vulture', 'hawk', 'strategy', 'authorization', 'split purse', 'rules'],
        blocks: [
          { type: 'p', text: 'The sub-program is where most decisions are made: the issuing bank, how cards are produced, renewal rules, healthcare handling, and — most importantly — the spending strategy. It has many settings, but each has a sensible default, so you only change what you need.' },
          { type: 'h', text: 'VULTURE vs HAWK — the spending strategy' },
          { type: 'terms', items: [
            { term: 'VULTURE', def: 'The card goes down a priority list of purses and pays from the first one allowed for that purchase. Targeted, no healthcare proof required.' },
            { term: 'HAWK', def: 'Same as Vulture, plus it enforces healthcare rules (IIAS). It can skip a purse if the item isn’t a supported healthcare category. Used for FSA/HSA-type benefits.' },
          ] },
          { type: 'analogy', text: 'Pay with a stack of labeled envelopes. VULTURE tries each in order until one is allowed to pay. HAWK does the same but also checks “is this an eligible medical item?” first — and demands proof.' },
          { type: 'h', text: 'One purchase, many purses' },
          { type: 'p', text: 'A “split purse” setting lets a single purchase draw from more than one purse when needed. If it’s off, only purchases payable from one purse are approved.' },
          { type: 'callout', tone: 'tip', title: 'You don’t memorize the fields', text: 'The tools ask in plain language and pick the right settings. The next module breaks the big sub-program settings into bite-size topics.' },
        ],
        cta: { label: 'Build a sub-program in the Guided Builder', section: 'acc-guided' },
      },
      {
        id: 'purses',
        title: 'Purses: pots of money',
        summary: 'The most-used building block — codes, plan periods, grace and priority.',
        minutes: 5,
        keywords: ['purse', 'pot', 'money', 'plan period', 'expiry', 'grace', 'priority', 'slot', 'purse code'],
        blocks: [
          { type: 'p', text: 'A purse is a labeled pot of money on the card. A single card can hold several purses. Purses are the thing you’ll touch most often.' },
          { type: 'h', text: 'The purse code' },
          { type: 'p', text: 'Each purse has a short code: three capital letters for the type, then four numbers (usually the year). FSA2026 means “Flexible Spending, 2026.” That code is how you point to a specific purse. Internally a purse also occupies a numbered “slot.”' },
          { type: 'h', text: 'Plan period & grace' },
          { type: 'p', text: 'A purse is active between a start date and an expiry date — its “plan period.” You can optionally add a short grace period (up to ~2.5 months) so in-flight purchases still go through just after expiry — in stores, in overnight batches, or both.' },
          { type: 'h', text: 'Priority' },
          { type: 'p', text: 'When a card has several purses, their priority order decides which pot is tried first. Active and future purses are prioritized; expired ones drop to the end.' },
          { type: 'callout', tone: 'rule', title: 'Purse code format', text: 'Exactly 3 capital letters + 4 digits (like FSA2026). The tools enforce this so a typo can’t slip through.' },
        ],
        cta: { label: 'Change a purse in the Setup Wizard', section: 'acc-wizard' },
      },
      {
        id: 'purse-spending',
        title: 'Purse spending rules & limits',
        summary: 'Cash tracks, velocity limits, transfers and merchant-name matching.',
        minutes: 5,
        keywords: ['cash track', 'atm', 'cashback', 'velocity', 'limit', 'min value', 'max value', 'transfer', 'otb', 'merchant name', 'wildcard'],
        blocks: [
          { type: 'p', text: 'Beyond dates and categories, a purse has fine-grained controls over how money moves.' },
          { type: 'h', text: 'Limits' },
          { type: 'list', items: [
            'Minimum/maximum balance — the smallest and largest the purse can hold.',
            'Minimum/maximum per load — caps on a single top-up.',
          ] },
          { type: 'h', text: 'Cash tracks' },
          { type: 'p', text: 'A purse can carry separate “cash tracks” for different kinds of cash access — General Purchase, ATM/Cash Disbursements (manual, automated, international), DDA transfer and Cashback — each with its own cycle and spend limits (per transaction, per cycle, lifetime).' },
          { type: 'h', text: 'Transfers (OTB)' },
          { type: 'p', text: 'On some programs, money can be transferred out of a purse (“open-to-buy” transfer) up to a maximum, monitored over a set number of days.' },
          { type: 'h', text: 'Merchant-name matching' },
          { type: 'p', text: 'A purse can also be qualified by merchant names using wildcards — e.g. “%walmart%” to match any Walmart. A purse uses either a network or a merchant-name group, not both.' },
          { type: 'callout', tone: 'tip', title: 'Start simple', text: 'Most purses just need a category group and dates. Reach for cash tracks and limits only when a benefit genuinely requires them.' },
        ],
      },
      {
        id: 'mcc-groups',
        title: 'Categories & MCC groups',
        summary: 'How FIS decides which stores a purse can be spent at.',
        minutes: 4,
        keywords: ['mcc', 'merchant category code', 'category', 'group', 'store', 'spend', 'high risk', '7995'],
        blocks: [
          { type: 'p', text: 'Every store is tagged with a four- or five-digit “Merchant Category Code” (MCC) describing what it sells. A pharmacy, a gas station and a dentist each have different MCCs.' },
          { type: 'p', text: 'Rather than list every store, FIS bundles MCCs into named “category groups.” A purse may spend wherever its category group permits. You can create and edit your own groups.' },
          { type: 'analogy', text: 'An MCC is a store’s genre label. A category group is a playlist of genres. A purse can only “play” stores whose genre is on its playlist.' },
          { type: 'callout', tone: 'warn', title: 'High-risk categories', text: 'Some categories (like betting/MCC 7995) are blocked by default for safety. You can choose to allow, or further restrict, specific MCCs on a sub-program.' },
          { type: 'callout', tone: 'tip', title: 'A very common task', text: 'Adding or removing MCCs from a group is one of the most frequent changes. The Setup Wizard handles it in two questions.' },
        ],
        cta: { label: 'Edit a category group', section: 'acc-wizard' },
      },
      {
        id: 'iias-groups',
        title: 'IIAS groups & the catalog',
        summary: 'The healthcare equivalent of category groups.',
        minutes: 3,
        keywords: ['iias group', 'rx', 'dental', 'vision', 'otc', 'ghc', 'nhc', 'catalog'],
        blocks: [
          { type: 'p', text: 'For healthcare purses, an “IIAS group” lists which eligible categories the purse accepts — prescriptions (Rx), dental, vision, over-the-counter (OTC), general healthcare (GHC), and non-healthcare (NHC).' },
          { type: 'p', text: 'FIS ships a large catalog of ready-made IIAS groups for every common combination (e.g. “Dental + Vision + Rx”, “Medical + Rx + OTC”). You can also create and manage your own.' },
          { type: 'analogy', text: 'IIAS groups are pre-built “healthcare playlists.” Instead of listing categories one by one, you pick the combo that matches the benefit.' },
          { type: 'callout', tone: 'info', title: 'Used with HAWK', text: 'IIAS groups only matter on HAWK sub-programs, where healthcare eligibility is enforced.' },
        ],
      },
    ],
  },

  // ============================================================ MODULE 3
  {
    id: 'cards', title: 'Cards & Production', icon: 'CreditCard',
    lessons: [
      {
        id: 'card-identity-auth',
        title: 'Card identity & cardholder verification',
        summary: 'Market segment, card type, and how cardholders prove who they are.',
        minutes: 4,
        keywords: ['market segment', 'card type', 'authenticate method', 'ssn', 'cvx2', 'verification', 'ivr', 'gift', 'money transfer'],
        blocks: [
          { type: 'p', text: 'Each sub-program declares what kind of program it is and how a cardholder is verified.' },
          { type: 'terms', items: [
            { term: 'Market segment', def: 'The purpose: Insurance, Incentive, Payroll, Gift, Tax Refund, and more.' },
            { term: 'Card type', def: 'The card flavor: Normal, Emergency Relief, Gift, Money Transfer, etc.' },
            { term: 'Authenticate method', def: 'What a cardholder uses to verify themselves on phone/web — last 4 of SSN, CVX2, last 4 of phone, zip, date of birth, a PIN, and others.' },
          ] },
          { type: 'analogy', text: 'Market segment and card type describe what the card is for; the authenticate method is the “password question” a cardholder answers to prove it’s them.' },
          { type: 'callout', tone: 'info', title: 'Sensible defaults', text: 'Most programs default to Insurance, a Normal card, and last-4-of-SSN verification. You only change these for special products.' },
        ],
      },
      {
        id: 'pin-management',
        title: 'PINs & PIN mailers',
        summary: 'How PINs are handled, seen, changed and protected.',
        minutes: 3,
        keywords: ['pin', 'pin mailer', 'lock', 'ivr', 'web', 'debit', 'retry'],
        blocks: [
          { type: 'p', text: 'For cards that use a PIN, the sub-program controls how the PIN is delivered and who can see or change it.' },
          { type: 'list', items: [
            'Whether the PIN arrives on the card carrier or in a separate PIN mailer (or only on request).',
            'Whether phone (IVR) and web can hear, see, or change the PIN — there are many fine-grained combinations.',
            'How many wrong attempts lock the PIN, and how many days until it unlocks.',
          ] },
          { type: 'callout', tone: 'tip', title: 'Debit-style only', text: 'These settings matter only when cards have PINs. Many benefit cards don’t, in which case you can ignore this.' },
        ],
      },
      {
        id: 'card-production',
        title: 'Card production & embossing',
        summary: 'What gets printed on the card and how it’s mailed.',
        minutes: 4,
        keywords: ['emboss', 'package', 'form factor', 'artwork', 'fulfillment', 'return address', 'duplicate card', 'carrier', 'oberthur'],
        blocks: [
          { type: 'p', text: 'A “package” is the recipe for a physical card — the plastic, artwork, carrier and inserts. Embossing settings control what’s printed and where.' },
          { type: 'h', text: 'Form factor' },
          { type: 'p', text: 'The card’s physical form: standard magnetic-stripe (Visa/MC or Discover), chip cards, or contactless options like a sticker, sleeve, watch/fob, or mobile phone.' },
          { type: 'h', text: 'What appears on the card & mailer' },
          { type: 'list', items: [
            'Whether to print the begin date and/or expiry date, and where on the card they sit.',
            'Up to four return-address lines on the mailer, and an optional fourth embossed line.',
            'Whether to include a duplicate card on the carrier (e.g. for a spouse).',
          ] },
          { type: 'terms', items: [
            { term: 'Fulfillment house', def: 'The company that physically prints and mails the cards.' },
            { term: 'Artwork name', def: 'The approved card design to use (e.g. “Zurich Benny Card”).' },
          ] },
          { type: 'callout', tone: 'warn', title: 'Match the package to the network', text: 'A package must match the card’s BIN — using a Mastercard package on a Visa BIN will fail to produce cards. FIS guards against this.' },
        ],
      },
      {
        id: 'renewals',
        title: 'Automatic card renewal',
        summary: 'Reissuing cards before they expire — and only when it makes sense.',
        minutes: 4,
        keywords: ['renew', 'auto renew', 'renewal window', 'replacement', 'expire', 'balance threshold'],
        blocks: [
          { type: 'p', text: 'Programs can automatically renew cards before they expire, so cardholders aren’t left without a card. There are smart conditions so you don’t reissue cards nobody is using.' },
          { type: 'list', items: [
            'How many days before expiry to start the renewal, and how long the new card lasts.',
            'Which card statuses qualify (e.g. Active, Suspended).',
            'Only renew if the card was used recently, or if the balance is above a threshold.',
            'Optionally use a different package for renewals than for the original card.',
          ] },
          { type: 'analogy', text: 'Auto-renewal is like a magazine subscription that re-sends only if you’re still reading — it checks recent activity and balances before printing a new card.' },
          { type: 'callout', tone: 'tip', title: 'Pairs with purse dates', text: 'Renewal works hand-in-hand with purse plan periods — there’s even a setting to renew only if a purse will still be active near the new card’s start.' },
        ],
      },
      {
        id: 'packages-links',
        title: 'Card numbers: BIN, PRIN & Agent',
        summary: 'How card numbers are organized and linked to a program.',
        minutes: 3,
        keywords: ['bin', 'prin', 'agent', 'package link', 'bpa', 'total number of cards'],
        blocks: [
          { type: 'p', text: 'Card numbers are organized in a hierarchy of their own. You rarely set these directly, but recognizing them helps.' },
          { type: 'terms', items: [
            { term: 'BIN', def: 'The bank/network number at the start of every card number — identifies the issuer.' },
            { term: 'PRIN', def: 'A sub-division within a BIN, used to separate programs.' },
            { term: 'Agent', def: 'A further range that reserves blocks of card numbers (each ~8,000 cards) for a program.' },
          ] },
          { type: 'analogy', text: 'BIN / PRIN / Agent are like country / city / street for card numbers — increasingly specific addresses telling FIS exactly where a program’s cards live.' },
          { type: 'callout', tone: 'info', title: 'Linking packages', text: 'To produce cards, a package is linked to a sub-program with a BIN/PRIN (and an agent range). This is usually done at launch; you’ll occasionally add or swap a package.' },
        ],
      },
    ],
  },

  // ============================================================ MODULE 4
  {
    id: 'health', title: 'Healthcare & Merchants', icon: 'HeartPulse',
    lessons: [
      {
        id: 'iias',
        title: 'IIAS: proving healthcare purchases',
        summary: 'How HAWK programs verify a purchase is truly eligible.',
        minutes: 4,
        keywords: ['iias', 'healthcare', 'substantiation', 'rx', 'dental', 'vision', 'otc', 'fsa', 'hsa', 'missing data'],
        blocks: [
          { type: 'p', text: 'For healthcare benefits, the IRS requires proof that money was spent on eligible items. “IIAS” is the system stores use to provide that proof automatically at checkout.' },
          { type: 'p', text: 'On a HAWK sub-program, FIS checks this proof. Each purse declares which healthcare categories it accepts (via an IIAS group).' },
          { type: 'analogy', text: 'IIAS is like a receipt that proves “yes, these items qualify.” HAWK won’t let the healthcare envelope pay unless that proof checks out.' },
          { type: 'callout', tone: 'info', title: 'When proof is missing', text: 'If an eligible purchase arrives without IIAS data, the program decides what to do — decline, fall back to older business logic, or treat it as non-healthcare — based on its settings.' },
        ],
      },
      {
        id: 'pbm-rtwa',
        title: 'Pharmacy matching (PBM & RTWA)',
        summary: 'Verifying prescription purchases against pharmacy data.',
        minutes: 4,
        keywords: ['pbm', 'pharmacy', 'rtwa', 'real time where available', 'conversion', 'rx', 'domain 3', 'silver', 'gold'],
        blocks: [
          { type: 'p', text: 'For prescription benefits, FIS can match transactions against a Pharmacy Benefit Manager’s (PBM) data to confirm they’re valid Rx purchases.' },
          { type: 'terms', items: [
            { term: 'PBM matching', def: 'Checking a purchase against pharmacy records. Can be required, optional (“match if possible”), or off.' },
            { term: 'RTWA', def: '“Real-Time Where Available” (Domain 3): match in real time when data exists, with rules for converting cards over time.' },
            { term: 'Fuzzy match', def: 'An optional tolerance so near-matches still approve, within a set dollar variance.' },
          ] },
          { type: 'analogy', text: 'PBM matching is a bouncer with a guest list (the pharmacy’s records). RTWA means the bouncer checks the live list when it’s available.' },
          { type: 'callout', tone: 'info', title: 'Specialized', text: 'These pharmacy features are mostly Domain 3 and set up with FIS. Good to recognize the terms when you see them.' },
        ],
      },
      {
        id: 'copay-paychase',
        title: 'Co-pay & post-purchase checks',
        summary: 'Co-pay validation, provider checks, and “Pay & Chase.”',
        minutes: 3,
        keywords: ['copay', 'co-pay', 'provider', 'merchant validation', 'carrier match', 'pay and chase', 'domain 12'],
        blocks: [
          { type: 'p', text: 'Some programs add extra validation around healthcare purchases (mostly Domain 12):' },
          { type: 'terms', items: [
            { term: 'Co-pay matching', def: 'Checks a purchase against expected co-pay amounts for certain categories.' },
            { term: 'Provider / merchant validation', def: 'Confirms the provider or merchant is recognized.' },
            { term: 'Carrier match (“Pay & Chase”)', def: 'Approves first, then substantiates afterward against a carrier’s data — chasing proof after payment.' },
          ] },
          { type: 'analogy', text: '“Pay & Chase” is paying the bill now and verifying the paperwork later, rather than holding up the line at checkout.' },
        ],
      },
      {
        id: 'merchants',
        title: 'Merchants, networks & remaps',
        summary: 'When category codes aren’t enough and you need named lists.',
        minutes: 5,
        keywords: ['merchant', 'network', 'retailer', 'remap', 'amcc', 'sparrow', 'list', 'business name'],
        blocks: [
          { type: 'p', text: 'Usually a purchase is judged by the store’s category (MCC). But sometimes that’s not enough — a store might report the wrong category, or you may want to allow/deny specific named merchants.' },
          { type: 'terms', items: [
            { term: 'Retailer', def: 'A “business name” record a merchant is filed under so it has a recognizable label (e.g. “Dental”).' },
            { term: 'Merchant', def: 'A specific merchant (or range of merchant numbers), optionally with an alternate MCC for remapping.' },
            { term: 'Remap (Alternate MCC / AMCC)', def: 'Treat a store as if it had a different category — fixing stores that report the wrong code.' },
            { term: 'Network', def: 'A named list a merchant belongs to; a purse references a network to recognize those merchants.' },
          ] },
          { type: 'analogy', text: 'Categories are the general rule; merchant lists are the exceptions. A remap says “treat this shop as a pharmacy”; a network is the named list a purse checks against.' },
          { type: 'callout', tone: 'tip', title: 'Build order', text: 'When setting these up the order is Retailer → Merchant → Merchant-Network. The tools and FIS examples follow this sequence.' },
        ],
      },
      {
        id: 'advanced-merchant-lists',
        title: 'Merchant-name groups & MTID lists',
        summary: 'Wildcard name lists, include/exclude terminals, and PBM/IIAS exceptions.',
        minutes: 4,
        keywords: ['merchant name list', 'wildcard', 'mtid', 'terminal', 'include', 'exclude', 'rtm exception', 'subprogram link'],
        blocks: [
          { type: 'p', text: 'A few more advanced merchant tools exist for precise control:' },
          { type: 'terms', items: [
            { term: 'Merchant-name lists', def: 'Lists of merchant names using “%” wildcards (e.g. %walmart%) to match by name rather than number.' },
            { term: 'MTID network', def: 'Force-allow or force-block exact merchants/terminals on a purse, overriding the normal category rules. Each purse can use up to 10 include/exclude lists.' },
            { term: 'RTM exception', def: 'Skip the real-time PBM/IIAS check for specific merchants (Base & Domain 3), optionally online-only or in-store-only, with date ranges.' },
          ] },
          { type: 'callout', tone: 'warn', title: 'Special characters in names', text: 'Names with &, <, > (and similar) can break a file. Keep merchant names clean — the tools block these characters automatically.' },
          { type: 'callout', tone: 'tip', title: 'Advanced', text: 'These are powerful but rarely needed for everyday changes. Start with category groups; reach for these for exceptions.' },
        ],
      },
      {
        id: 'statements',
        title: 'Statements (online & paper)',
        summary: 'Turning cardholder statements on, and the rules for printing them.',
        minutes: 3,
        keywords: ['statement', 'online', 'paper', 'print rules', 'template', 'harland', 'opt in', 'close day'],
        blocks: [
          { type: 'p', text: 'A sub-program can generate online and/or paper statements, with control over when and which accounts get them.' },
          { type: 'list', items: [
            'Online, paper, or both — and whether cardholders must opt in to paper.',
            'The monthly close day, and the date statements start generating.',
            'Print rules: only if there was activity, only for certain card statuses, or skip zero-balance accounts.',
            'Paper type, template, and print vendor/format.',
          ] },
          { type: 'callout', tone: 'warn', title: 'Paper needs extra setup', text: 'Turning on paper statements involves additional steps — always check with your FIS contact before enabling it.' },
        ],
      },
    ],
  },

  // ============================================================ MODULE 5
  {
    id: 'money', title: 'Money Movement & Products', icon: 'ArrowLeftRight',
    lessons: [
      {
        id: 'transfers',
        title: 'Card-to-card transfers',
        summary: 'Letting cardholders move value between cards or purses.',
        minutes: 4,
        keywords: ['transfer', 'account to account', 'a2a', 'sender', 'receiver', 'mfa', 'search', 'limit'],
        blocks: [
          { type: 'p', text: 'Some programs let value move between cards in the same program — for example from a master funding account to cardholder cards, or between cardholders.' },
          { type: 'p', text: 'Transfers are tightly controlled on both ends. You set sender and receiver rules separately:' },
          { type: 'list', items: [
            'Limits — max per transaction, per day, and per period; min amounts; how many recipients.',
            'Which card statuses qualify, and whether fees can be reversed.',
            'How a recipient is found — by email, by phone, or by a search rule (e.g. most recent activation).',
          ] },
          { type: 'analogy', text: 'Think of it like a controlled “send money” feature: you decide who can send, who can receive, how much, how often, and how they look each other up.' },
        ],
      },
      {
        id: 'direct-access',
        title: 'Direct Access (ACH)',
        summary: 'Giving a program account/routing numbers for bank transfers.',
        minutes: 3,
        keywords: ['direct access', 'ach', 'da', 'bank prefix', 'trn', 'routing', 'deposit', 'withdrawal'],
        blocks: [
          { type: 'p', text: 'Direct Access (DA) gives a program the plumbing for bank transfers (ACH) — so funds can be loaded or moved via account and routing numbers.' },
          { type: 'terms', items: [
            { term: 'Bank prefix', def: 'A short code that forms part of the account number.' },
            { term: 'TRN', def: 'The transit routing number used for ACH.' },
            { term: 'Withdrawal setting', def: 'Whether withdrawals are allowed, blocked, or restricted.' },
          ] },
          { type: 'callout', tone: 'rule', title: 'Set up a purse first', text: 'Direct Access must follow a purse — it points incoming deposits at a default purse. Turning DA off won’t stop incoming files, but it stops new accounts getting DA numbers.' },
        ],
      },
      {
        id: 'filtered-spend',
        title: 'Filtered Spend (APL & funding accounts)',
        summary: 'Restricting spend to an approved product list, funded centrally.',
        minutes: 3,
        keywords: ['filtered spend', 'apl', 'approved product list', 'mfa', 'master funding account', 'sweep'],
        blocks: [
          { type: 'p', text: 'The “Filtered Spend” product restricts what a card can buy to an Approved Product List (APL), and can fund cards from a central Master Funding Account (MFA).' },
          { type: 'terms', items: [
            { term: 'APL (Approved Product List)', def: 'A defined list of allowed products; linked to a sub-program.' },
            { term: 'MFA (Master Funding Account)', def: 'A central pot that funds individual cards, with an optional automatic “sweep.”' },
          ] },
          { type: 'analogy', text: 'Filtered Spend is a card that only works on a pre-approved shopping list, topped up from one shared wallet.' },
          { type: 'callout', tone: 'info', title: 'Product-specific', text: 'These nodes apply only to Filtered Spend programs. If that’s not your product, you can skip this.' },
        ],
      },
      {
        id: 'consumer-elections',
        title: 'Consumer elections',
        summary: 'Letting cardholders choose how categories map to their purses.',
        minutes: 2,
        keywords: ['consumer election', 'include', 'exclude', 'group', 'choice'],
        blocks: [
          { type: 'p', text: 'Consumer elections let a program define groups (by MCC and IIAS group) that a cardholder’s choices map to — including or excluding certain spending for a purse.' },
          { type: 'analogy', text: 'It’s like letting a member toggle which buckets their spending counts toward, within rules you define.' },
          { type: 'callout', tone: 'info', title: 'Turns on automatically', text: 'Enabling consumer elections at the purse level switches on the related sub-program flag for you.' },
        ],
      },
      {
        id: 'reload-priority',
        title: 'Default purse, priorities & reload networks',
        summary: 'Where reloaded money lands, and overriding purse priority.',
        minutes: 3,
        keywords: ['default purse', 'priority set', 'reload', 'repower', 'readylink'],
        blocks: [
          { type: 'p', text: 'When money comes in from a reload network (like RePower or ReadyLink), it needs a home. The “default purse” sets where those loads land.' },
          { type: 'p', text: 'You can also define “purse priority sets” at the sub-program level to override the order purses are tried — without changing each card individually.' },
          { type: 'callout', tone: 'warn', title: 'Don’t point at an expired purse', text: 'If the chosen default purse is expired, the load is rejected — keep the default current.' },
        ],
      },
    ],
  },

  // ============================================================ MODULE 6
  {
    id: 'controls', title: 'Risk, Fees & Controls', icon: 'Gauge',
    lessons: [
      {
        id: 'fees',
        title: 'Card fees',
        summary: 'Replacement and reissue fees, and the rules around them.',
        minutes: 2,
        keywords: ['fee', 'replacement', 'reissue', 'lost stolen', 'transportation', 'irs'],
        blocks: [
          { type: 'p', text: 'Two fees are configurable per sub-program: the Card Replacement fee (lost/stolen) and the Card Reissue fee (expired/renewed). They default to $10 and must be under $20. Enter 0 to remove a fee.' },
          { type: 'callout', tone: 'info', title: 'Transportation purses', text: 'On some programs, if a transportation purse is detected these fees default to $0 because IRS rules don’t permit them.' },
          { type: 'callout', tone: 'tip', title: 'Wizard-friendly', text: 'The Setup Wizard changes a fee in two questions — no structure to learn.' },
        ],
        cta: { label: 'Change a fee', section: 'acc-wizard' },
      },
      {
        id: 'risk-tools',
        title: 'Fraud & risk tools',
        summary: 'IDology, Falcon and excessive-credit monitoring.',
        minutes: 3,
        keywords: ['risk', 'fraud', 'idology', 'falcon', 'excessive credits', 'travel indicator', 'scoring'],
        blocks: [
          { type: 'p', text: 'FIS offers fraud-screening tools you can enable per program:' },
          { type: 'terms', items: [
            { term: 'IDology', def: 'Identity-verification screening; can be copied from one program to another.' },
            { term: 'Falcon', def: 'Real-time fraud scoring (with options like travel-indicator scoring).' },
            { term: 'Excessive-credits monitoring', def: 'Watches for unusual credit/refund patterns; configuration can be cloned between programs.' },
          ] },
          { type: 'callout', tone: 'info', title: 'Set up with FIS', text: 'Risk tools usually involve contracts and FIS setup — you mainly enable and clone them.' },
        ],
      },
      {
        id: 'countries-address',
        title: 'Country blocks & address controls',
        summary: 'Geography rules and anti-fraud limits on address changes.',
        minutes: 3,
        keywords: ['country', 'block', 'blocked countries', 'address', 'velocity', 'freeze', 'exempt'],
        blocks: [
          { type: 'h', text: 'Country blocks' },
          { type: 'p', text: 'You can block (or re-allow) spending in a specific country by its numeric code, and choose what cardholders see on a blocked purchase — “service restricted,” a decline, or an approval.' },
          { type: 'h', text: 'Address-change velocity' },
          { type: 'p', text: 'To deter fraud, you can freeze address changes for a number of days after activation, limit how many changes are allowed in a window, require documents to replace/reissue a card, and exempt trusted sources.' },
          { type: 'callout', tone: 'tip', title: 'Wizard-friendly', text: 'Blocking or re-allowing a country is a short guided flow in the Setup Wizard.' },
        ],
        cta: { label: 'Block or allow a country', section: 'acc-wizard' },
      },
    ],
  },

  // ============================================================ MODULE 7
  {
    id: 'safe', title: 'Doing It Safely', icon: 'Wrench',
    lessons: [
      {
        id: 'perform-action',
        title: 'Add, change, or remove?',
        summary: 'The single most important concept: what action you’re taking.',
        minutes: 3,
        keywords: ['performaction', 'add', 'update', 'overwrite', 'delete', 'action', 'change'],
        blocks: [
          { type: 'p', text: 'Every instruction says whether you’re creating something new, changing something that exists, or removing it. Behind the scenes this is a number, but you always pick it as a plain action.' },
          { type: 'table', head: ['You mean…', 'What it does'], rows: [
            ['Add', 'Create something brand-new. It must not already exist.'],
            ['Change / Overwrite', 'Update something that exists. You only fill in what changes.'],
            ['Remove / Delete', 'Take something away.'],
          ] },
          { type: 'callout', tone: 'warn', title: 'Add vs Change matters', text: 'If you “Add” something that already exists, FIS rejects it (no duplicates). If you “Change” something that doesn’t exist, there’s nothing to update. The right action avoids most errors.' },
          { type: 'callout', tone: 'info', title: 'The tools choose the code', text: 'You choose “Add / Change / Remove” in words; the app fills in the correct technical code, which varies slightly by item.' },
        ],
      },
      {
        id: 'golden-rules',
        title: 'The golden rules (avoid these mistakes)',
        summary: 'A short list of gotchas that cause most rejected files.',
        minutes: 4,
        keywords: ['rules', 'special characters', 'order', 'nested', 'all or nothing', 'date', 'file name', 'gotcha'],
        blocks: [
          { type: 'callout', tone: 'rule', title: '1. Avoid these characters', text: 'The symbols & < > " # and apostrophes can break the whole file. The tools block them — but if you ever hand-edit, keep them out of names and descriptions.' },
          { type: 'callout', tone: 'rule', title: '2. Top-down order', text: 'Parents before children: client → program → sub-program → purse. The tools order things correctly for you.' },
          { type: 'callout', tone: 'rule', title: '3. Nested = all-or-nothing', text: 'If you bundle changes together and one fails, the whole bundle is undone. Helpful for safety, but worth knowing.' },
          { type: 'callout', tone: 'rule', title: '4. Dates are year-month-day', text: 'Always YYYY-MM-DD (e.g. 2026-12-31). Date pickers in the tools do this for you.' },
          { type: 'callout', tone: 'rule', title: '5. File names: letters, numbers, underscores', text: 'No spaces, must end in .XML. A bad name can jam the loader. The tools clean names automatically.' },
        ],
      },
      {
        id: 'formatting-clean',
        title: 'How text gets cleaned',
        summary: 'Why what you type may look different on the printed card.',
        minutes: 2,
        keywords: ['clean', 'formatting', 'accent', 'uppercase', 'characters', 'emboss'],
        blocks: [
          { type: 'p', text: 'Text destined for the printed card runs through a “clean” step so it can be embossed reliably. By default it:' },
          { type: 'list', items: [
            'Converts lowercase to uppercase.',
            'Maps accented letters to plain ones (é → E, ñ → N, ü → U, and so on).',
            'Replaces any other unsupported character with a space, and collapses double spaces.',
          ] },
          { type: 'analogy', text: 'It’s like a printer that only stocks plain block capitals — fancy characters get translated to their closest plain equivalent.' },
          { type: 'callout', tone: 'info', title: 'Can be relaxed', text: 'With agreement between you, FIS and the card printer, some of this (lowercase, special characters, spacing) can be preserved.' },
        ],
      },
      {
        id: 'results',
        title: 'Reading results & fixing errors',
        summary: 'Confirm a change worked — and what to do if it didn’t.',
        minutes: 4,
        keywords: ['log', 'error', 'response', 'result', 'resubmit', 'schema', 'logic', 'batch failure', 'crm'],
        blocks: [
          { type: 'p', text: 'After processing, FIS returns two files in “AccLogs,” each named after yours:' },
          { type: 'terms', items: [
            { term: 'Log.<yourfile>', def: 'An echo of everything that ran. Successful items show “Request processed successfully.”' },
            { term: 'Error.<yourfile>', def: 'Only the items that failed, each with a reason you can act on.' },
          ] },
          { type: 'h', text: 'Kinds of errors' },
          { type: 'terms', items: [
            { term: 'Schema errors', def: 'The format was off — a value out of range, missing field, or wrong order.' },
            { term: 'Logic errors', def: 'The format was fine but the request didn’t make sense — e.g. a duplicate name.' },
            { term: 'Node / batch failures', def: 'A system hiccup (like a timeout). Usually you just resend.' },
          ] },
          { type: 'steps', items: [
            { title: 'Open the Error file first', text: 'Empty means everything worked. Otherwise read the reason next to each failed item.' },
            { title: 'Fix the cause', text: 'Most issues are simple — a name that doesn’t exist, the wrong action, a value out of range.' },
            { title: 'Resend just the fixes', text: 'Reuse the Error file as a starting point: correct it, rename it, and submit again.' },
          ] },
          { type: 'callout', tone: 'tip', title: 'When in doubt, ask', text: 'For unclear errors, missing names, or special setups, your FIS Client Relations Manager (CRM) is the right contact.' },
        ],
      },
    ],
  },
];

export const GLOSSARY = [
  { term: 'ACC', short: 'Automated Client Configuration — the way you send setup instructions to FIS via a file.' },
  { term: 'MoveIT', short: 'The secure folder (“landing zone”) where you drop instruction files for FIS to pick up.' },
  { term: 'SysAdmiral', short: 'The FIS process that detects and runs dropped files (with a 1–15 minute delay).' },
  { term: 'Batch', short: 'A whole instruction file, addressed to your company and wrapping all the nodes.' },
  { term: 'Node', short: 'One instruction block in a file — a Client, Purse, Fee, and so on.' },
  { term: 'Client', short: 'A company or group at the top of the FIS hierarchy (levels 2–9).' },
  { term: 'Program', short: 'A benefits offering belonging to a client; mostly carries dates and a report name.' },
  { term: 'Sub-program', short: 'A specific variant of a program with its own rules — where most setup happens.' },
  { term: 'Purse', short: 'A labeled pot of money on a card. Cards can hold several purses.' },
  { term: 'Purse code', short: 'A purse’s short ID: 3 letters + 4 digits, e.g. FSA2026.' },
  { term: 'Plan period', short: 'The start-to-expiry window during which a purse can be spent.' },
  { term: 'Grace period', short: 'A short window after expiry when spending is still allowed.' },
  { term: 'Cash track', short: 'A purse’s rules for cash-type access (ATM, cashback, etc.) with its own limits.' },
  { term: 'OTB transfer', short: '“Open-to-buy” — moving available balance out of a purse, within limits.' },
  { term: 'MCC', short: 'Merchant Category Code — a number describing what a store sells.' },
  { term: 'MCC group', short: 'A named bundle of MCCs that defines where a purse can be spent.' },
  { term: 'VULTURE', short: 'A spending strategy: pay from the first allowed purse in priority order.' },
  { term: 'HAWK', short: 'Like Vulture, plus healthcare (IIAS) checks; used for FSA/HSA benefits.' },
  { term: 'Split purse', short: 'Allowing one purchase to draw from more than one purse.' },
  { term: 'IIAS', short: 'The system stores use to prove a purchase is an eligible healthcare item.' },
  { term: 'IIAS group', short: 'A pre-built list of eligible healthcare categories a purse accepts.' },
  { term: 'Substantiation', short: 'Proving a healthcare purchase qualified — what IIAS provides.' },
  { term: 'PBM', short: 'Pharmacy Benefit Manager — used to validate prescription purchases.' },
  { term: 'RTWA', short: '“Real-Time Where Available” — match pharmacy data live when it exists (Domain 3).' },
  { term: 'Co-pay matching', short: 'Validating a purchase against expected co-pay amounts.' },
  { term: 'Pay & Chase', short: 'Approve first, then substantiate afterward (carrier match).' },
  { term: 'BIN', short: 'The issuer/network number at the start of a card number.' },
  { term: 'PRIN', short: 'A sub-division within a BIN used to separate programs.' },
  { term: 'Agent', short: 'A range used to reserve blocks of card numbers for a program.' },
  { term: 'Package', short: 'The “recipe” for a physical card: plastic, artwork, carrier and inserts.' },
  { term: 'Form factor', short: 'The card’s physical type: mag-stripe, chip, contactless sticker/fob/mobile, etc.' },
  { term: 'Fulfillment house', short: 'The company that physically prints and mails the cards.' },
  { term: 'Authenticate method', short: 'How a cardholder verifies identity (last-4 SSN, CVX2, PIN, etc.).' },
  { term: 'Remap (AMCC)', short: 'Treating a store as a different category to fix mis-reported codes.' },
  { term: 'Retailer', short: 'A business-name record a merchant is filed under for a recognizable label.' },
  { term: 'Merchant network', short: 'A named list of specific merchants to allow or check.' },
  { term: 'MTID network', short: 'A list to force-allow or force-block exact merchants/terminals on a purse.' },
  { term: 'RTM exception', short: 'Skipping the real-time PBM/IIAS check for specific merchants.' },
  { term: 'Direct Access (ACH)', short: 'Account/routing numbers that let funds move to a program by bank transfer.' },
  { term: 'Filtered Spend', short: 'A card restricted to an approved product list, often centrally funded.' },
  { term: 'APL', short: 'Approved Product List — the allowed items for a Filtered Spend card.' },
  { term: 'MFA', short: 'Master Funding Account — a central pot that funds individual cards.' },
  { term: 'Consumer election', short: 'Letting cardholders map their spending to defined groups.' },
  { term: 'RePower / ReadyLink', short: 'Reload networks that add money to cards; the default purse decides where it lands.' },
  { term: 'Falcon', short: 'A real-time fraud-scoring tool.' },
  { term: 'IDology', short: 'An identity-verification screening tool.' },
  { term: 'PerformAction', short: 'Whether an instruction adds, changes, or removes something.' },
  { term: 'Clean routine', short: 'The step that upper-cases and simplifies text for card embossing.' },
  { term: 'Log file', short: 'FIS’s echo of everything your file did, successful items included.' },
  { term: 'Error file', short: 'FIS’s list of only the items that failed, with reasons.' },
  { term: 'CRM', short: 'Client Relations Manager — your main FIS contact for questions.' },
  { term: 'Reg E', short: 'A US consumer-protection rule some programs follow for disputes.' },
  { term: 'Domain', short: 'An internal FIS grouping (e.g. 3, 12, base) that sets default behaviors.' },
];

// ---- search index helpers --------------------------------------------------
function blockText(b) {
  switch (b.type) {
    case 'p': case 'h': case 'analogy': return b.text || '';
    case 'callout': return `${b.title || ''} ${b.text || ''}`;
    case 'list': return (b.items || []).join(' ');
    case 'steps': return (b.items || []).map((i) => `${i.title} ${i.text}`).join(' ');
    case 'terms': return (b.items || []).map((i) => `${i.term} ${i.def}`).join(' ');
    case 'faq': return (b.items || []).map((i) => `${i.q} ${i.a}`).join(' ');
    case 'table': return [...(b.head || []), ...(b.rows || []).flat()].join(' ');
    default: return '';
  }
}

export const ALL_LESSONS = COURSE.flatMap((m) =>
  m.lessons.map((l) => ({ ...l, moduleId: m.id, moduleTitle: m.title })));

const SEARCH_INDEX = ALL_LESSONS.map((l) => ({
  lesson: l,
  text: [l.title, l.summary, (l.keywords || []).join(' '), ...l.blocks.map(blockText)].join(' ').toLowerCase(),
}));

export function searchCourse(query) {
  const q = query.trim().toLowerCase();
  if (!q) return { lessons: [], terms: [] };
  const lessons = SEARCH_INDEX.filter((e) => e.text.includes(q)).map((e) => e.lesson);
  const terms = GLOSSARY.filter((g) => `${g.term} ${g.short}`.toLowerCase().includes(q));
  return { lessons, terms };
}
